import React, { useEffect, useRef, useState, useCallback } from 'react';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Stack from '@mui/material/Stack';
import Autocomplete from '@mui/material/Autocomplete';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { ptBR } from 'date-fns/locale';
import {
  getIndicadores,
  getGruposFundo,
  createGrupoFundo,
  createAtivoMaster,
  updateAtivoMaster,
  createDeParaAtivo,
  searchAtivosMaster,
  getCronogramasByAtivoMaster,
  createCronogramaAmortizacao,
  updateCronogramaAmortizacao,
  deleteCronogramaAmortizacao,
} from '../services/ativoMasterService';
import {
  Indicador,
  GrupoFundo,
  AtivoMaster,
  RentTipo,
  RentPeriodo,
  ClasseFIDC,
  CronogramaTipo,
  CronogramaPeriodo,
} from '../types/ativoMaster';
import { getErrorMessage } from '../utils/errorHandler';

// ─── Props ───────────────────────────────────────────────────────────────────

interface CadastrarAtivoMasterModalProps {
  isOpen: boolean;
  onRequestClose: () => void;
  /** Se fornecido, abre em modo edição do ativo existente */
  ativoMasterExistente?: AtivoMaster;
  /** Dados pré-preenchidos da linha da carteira (para auto-criar DeParaAtivo) */
  dadosCarteira?: {
    administradora: string;
    codigo_bruto: string;
    nome_bruto: string;
  };
  /** Grupo pré-selecionado ao criar novo ativo (usado na página de Mapeamento) */
  grupoPreSelecionado?: GrupoFundo;
  onSalvo: (ativo: AtivoMaster) => void;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const TIPO_FUNDO_OPTIONS = [
  { value: 'FIDC', label: 'FIDC' },
  { value: 'FIM', label: 'FIM' },
  { value: 'FIA', label: 'FIA' },
  { value: 'FIP', label: 'FIP' },
  { value: 'FIRF', label: 'FIRF' },
  { value: 'FII', label: 'FII' },
  { value: 'ETF', label: 'ETF' },
  { value: 'OUTROS', label: 'Outros' },
];

const CONDOMINIO_OPTIONS = [
  { value: 'ABERTO', label: 'Aberto' },
  { value: 'FECHADO', label: 'Fechado' },
];

const RENT_TIPO_OPTIONS: { value: RentTipo; label: string }[] = [
  { value: 'FIXO', label: 'Taxa Fixa' },
  { value: 'IND_PERCENT', label: '% do Indicador' },
  { value: 'IND_SPREAD', label: 'Indicador + Spread' },
];

const RENT_PERIODO_OPTIONS: { value: RentPeriodo; label: string }[] = [
  { value: 'AA', label: 'Ao Ano (a.a.)' },
  { value: 'AM', label: 'Ao Mês (a.m.)' },
];

const CLASSE_FIDC_OPTIONS: { value: ClasseFIDC; label: string }[] = [
  { value: 'SENIOR', label: 'Sênior' },
  { value: 'MEZANINO', label: 'Mezanino' },
  { value: 'SUBORDINADA', label: 'Subordinada' },
  { value: 'JUNIOR', label: 'Júnior' },
];

// ─── Styles ──────────────────────────────────────────────────────────────────

const modalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '100%',
  maxWidth: 720,
  maxHeight: '92vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  overflowY: 'auto',
  borderRadius: 2,
};

const sectionTitle = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 600,
  fontSize: '13px',
  color: '#4D5766',
  mb: 1.5,
  mt: 0,
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatCNPJ = (value: string): string => {
  const raw = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 14);
  if (raw.length <= 2) return raw;
  if (raw.length <= 5) return `${raw.slice(0, 2)}.${raw.slice(2)}`;
  if (raw.length <= 8) return `${raw.slice(0, 2)}.${raw.slice(2, 5)}.${raw.slice(5)}`;
  if (raw.length <= 12) return `${raw.slice(0, 2)}.${raw.slice(2, 5)}.${raw.slice(5, 8)}/${raw.slice(8)}`;
  return `${raw.slice(0, 2)}.${raw.slice(2, 5)}.${raw.slice(5, 8)}/${raw.slice(8, 12)}-${raw.slice(12)}`;
};

const monthToDate = (s: string): Date | null => {
  if (!s) return null;
  const parts = s.split('-');
  return new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, 1);
};

const dateToMonth = (d: Date | null): string => {
  if (!d || isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

// ─── Cronograma ──────────────────────────────────────────────────────────────

const TIPO_OPTIONS: { value: CronogramaTipo; label: string }[] = [
  { value: 'JUROS',           label: 'Pagamento de Juros' },
  { value: 'PRINCIPAL',       label: 'Amortização de Principal' },
  { value: 'PRINCIPAL_JUROS', label: 'Principal + Juros' },
  { value: 'RESGATE_TOTAL',   label: 'Resgate Total' },
  { value: 'PERSONALIZADO',   label: 'Personalizado' },
];

const PERIODO_OPTIONS: { value: CronogramaPeriodo; label: string }[] = [
  { value: 'MENSAL',        label: 'Mensal' },
  { value: 'TRIMESTRAL',    label: 'Trimestral' },
  { value: 'SEMESTRAL',     label: 'Semestral' },
  { value: 'ANUAL',         label: 'Anual' },
  { value: 'NO_VENCIMENTO', label: 'No Vencimento' },
];

interface CronogramaFormData {
  id?: number;
  tipo: CronogramaTipo;
  descricao: string;
  periodo: string;
  mes_ano_inicio: string; // YYYY-MM
  mes_ano_fim: string;    // YYYY-MM (vazio quando não aplicável)
  dia: string;            // "" ou número como string
  _dirty?: boolean;       // marcado true ao editar; evita PATCH desnecessário
}

type CronogramaEditableField = Exclude<keyof CronogramaFormData, '_dirty' | 'id'>;

const EMPTY_CRON_ENTRY = (): CronogramaFormData => ({
  tipo: 'JUROS', descricao: '', periodo: '', mes_ano_inicio: '', mes_ano_fim: '', dia: '',
});

// ─── Sub-form: Novo GrupoFundo ────────────────────────────────────────────────

interface NovoGrupoFormState {
  nome: string;
  cnpj: string;
  gestor: string;
  administrador: string;
  tipo: string;
}

const EMPTY_GRUPO: NovoGrupoFormState = {
  nome: '', cnpj: '', gestor: '', administrador: '', tipo: '',
};

// ─── Main Component ──────────────────────────────────────────────────────────

const CadastrarAtivoMasterModal: React.FC<CadastrarAtivoMasterModalProps> = ({
  isOpen,
  onRequestClose,
  ativoMasterExistente,
  dadosCarteira,
  grupoPreSelecionado,
  onSalvo,
}) => {
  const isEdicao = !!ativoMasterExistente;

  // Listas de opções
  const [indicadores, setIndicadores] = useState<Indicador[]>([]);
  const [gruposFundo, setGruposFundo] = useState<GrupoFundo[]>([]);
  const [loadingOpcoes, setLoadingOpcoes] = useState(false);

  // Formulário principal
  const [nomeCanonical, setNomeCanonical] = useState('');
  const [grupoFundoSelecionado, setGrupoFundoSelecionado] = useState<GrupoFundo | null>(null);
  const [classeFidc, setClasseFidc] = useState<ClasseFIDC | ''>('');
  const [subclasse, setSubclasse] = useState('');
  const [condominio, setCondominio] = useState('');
  const [rentTipo, setRentTipo] = useState<RentTipo | ''>('');
  const [rentValor, setRentValor] = useState('');
  const [rentPeriodo, setRentPeriodo] = useState<RentPeriodo>('AA');
  const [rentIndicador, setRentIndicador] = useState<Indicador | null>(null);
  const [prazoResgate, setPrazoResgate] = useState('');
  const [observacoes, setObservacoes] = useState('');

  // Criação de novo GrupoFundo inline
  const [mostrarNovoGrupo, setMostrarNovoGrupo] = useState(false);
  const [novoGrupo, setNovoGrupo] = useState<NovoGrupoFormState>(EMPTY_GRUPO);
  const [salvandoGrupo, setSalvandoGrupo] = useState(false);

  // "Vincular a existente" — só disponível quando dadosCarteira está presente
  const [vincularExistente, setVincularExistente] = useState(false);
  const [ativosMasterOpcoes, setAtivosMasterOpcoes] = useState<AtivoMaster[]>([]);
  const [loadingAtivosMaster, setLoadingAtivosMaster] = useState(false);
  const [ativoExistenteSelecionado, setAtivoExistenteSelecionado] = useState<AtivoMaster | null>(null);
  const searchMasterTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cronograma (somente condomínio FECHADO)
  const [cronogramas,           setCronogramas]           = useState<CronogramaFormData[]>([]);
  const [cronogramaObservacoes, setCronogramaObservacoes] = useState('');
  const [deletedCronIds,        setDeletedCronIds]        = useState<number[]>([]);
  const [loadingCron,           setLoadingCron]           = useState(false);

  // Estado geral
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  // Carrega listas ao abrir o modal
  useEffect(() => {
    if (!isOpen) return;
    setLoadingOpcoes(true);
    setErro(null);
    Promise.all([getIndicadores(), getGruposFundo()])
      .then(([inds, grupos]) => {
        setIndicadores(inds);
        setGruposFundo(grupos);
      })
      .catch(() => setErro('Erro ao carregar opções.'))
      .finally(() => setLoadingOpcoes(false));
  }, [isOpen]);

  // Preenche campos quando estiver em modo edição
  useEffect(() => {
    if (!isOpen) return;
    if (ativoMasterExistente) {
      setNomeCanonical(ativoMasterExistente.nome_canonico);
      setClasseFidc((ativoMasterExistente.classe_fidc as ClasseFIDC) ?? '');
      setSubclasse(ativoMasterExistente.subclasse ?? '');
      setCondominio(ativoMasterExistente.condominio ?? '');
      setRentTipo((ativoMasterExistente.rent_tipo as RentTipo) ?? '');
      setRentValor(ativoMasterExistente.rent_valor != null ? String(ativoMasterExistente.rent_valor) : '');
      setRentPeriodo(ativoMasterExistente.rent_periodo ?? 'AA');
      setPrazoResgate(ativoMasterExistente.prazo_resgate ?? '');
      setObservacoes(ativoMasterExistente.observacoes ?? '');
      setCronogramaObservacoes(ativoMasterExistente.cronograma_observacoes ?? '');
    } else {
      // modo criação — deixa o nome vazio para que o auto-preenchimento pelo grupo funcione sem conflito
      setNomeCanonical('');
      nomeAutoGeradoRef.current = '';
      setClasseFidc('');
      setSubclasse('');
      setCondominio('');
      setRentTipo('');
      setRentValor('');
      setRentPeriodo('AA');
      setPrazoResgate('');
      setObservacoes('');
      setCronogramaObservacoes('');
      // pré-seleciona o grupo se vier da página de Mapeamento
      if (grupoPreSelecionado) {
        setGrupoFundoSelecionado(grupoPreSelecionado);
      } else {
        setGrupoFundoSelecionado(null);
      }
    }
    setMostrarNovoGrupo(false);
    setNovoGrupo(EMPTY_GRUPO);
    nomeAutoGeradoRef.current = '';
    setVincularExistente(false);
    setAtivoExistenteSelecionado(null);
    setAtivosMasterOpcoes([]);
    setCronogramas([]);
    setDeletedCronIds([]);
  }, [isOpen, ativoMasterExistente, dadosCarteira, grupoPreSelecionado]);

  // Carrega cronogramas ao abrir em modo edição (somente FECHADO)
  useEffect(() => {
    if (!isOpen || !isEdicao || !ativoMasterExistente || ativoMasterExistente.condominio !== 'FECHADO') return;
    setLoadingCron(true);
    getCronogramasByAtivoMaster(ativoMasterExistente.id)
      .then(lista => {
        setCronogramas(lista.map(c => ({
          id:             c.id,
          tipo:           c.tipo,
          descricao:      c.descricao ?? '',
          periodo:        c.periodo,
          mes_ano_inicio: c.mes_ano_inicio.slice(0, 7),
          mes_ano_fim:    c.mes_ano_fim ? c.mes_ano_fim.slice(0, 7) : '',
          dia:            c.dia != null ? String(c.dia) : '',
        })));
        setDeletedCronIds([]);
      })
      .catch(() => setErro('Erro ao carregar cronograma de amortização.'))
      .finally(() => setLoadingCron(false));
  }, [isOpen, ativoMasterExistente?.id]);

  // Sincroniza grupo/indicador quando as listas forem carregadas (modo edição)
  useEffect(() => {
    if (!ativoMasterExistente) return;
    if (gruposFundo.length && ativoMasterExistente.grupo_fundo) {
      setGrupoFundoSelecionado(
        gruposFundo.find(g => g.id === ativoMasterExistente.grupo_fundo) ?? null
      );
    }
    if (indicadores.length && ativoMasterExistente.rent_indicador) {
      setRentIndicador(
        indicadores.find(i => i.id === ativoMasterExistente.rent_indicador) ?? null
      );
    }
  }, [gruposFundo, indicadores, ativoMasterExistente]);

  const grupoEhFidc = grupoFundoSelecionado?.tipo === 'FIDC';
  const rentPrecisaIndicador = rentTipo === 'IND_PERCENT' || rentTipo === 'IND_SPREAD';
  const rentPrecisaValor = !!rentTipo;

  // Auto-gera nome canônico a partir do grupo + classe + subclasse (apenas em criação)
  const nomeAutoGeradoRef = useRef('');
  useEffect(() => {
    if (isEdicao) return;
    if (!grupoFundoSelecionado) {
      if (nomeCanonical === nomeAutoGeradoRef.current) setNomeCanonical('');
      nomeAutoGeradoRef.current = '';
      return;
    }
    let sugestao = grupoFundoSelecionado.nome;
    if (grupoEhFidc && classeFidc) {
      const label = CLASSE_FIDC_OPTIONS.find(o => o.value === classeFidc)?.label ?? classeFidc;
      sugestao += ` — ${label}`;
      if (subclasse) sugestao += ` ${subclasse}`;
    } else if (!grupoEhFidc && subclasse) {
      sugestao += ` — ${subclasse}`;
    }
    setNomeCanonical(prev => (prev === '' || prev === nomeAutoGeradoRef.current) ? sugestao : prev);
    nomeAutoGeradoRef.current = sugestao;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grupoFundoSelecionado?.id, classeFidc, subclasse, grupoEhFidc, isEdicao]);

  const handleCriarGrupo = async () => {
    if (!novoGrupo.nome.trim()) return;
    setSalvandoGrupo(true);
    try {
      const criado = await createGrupoFundo({
        nome: novoGrupo.nome.trim(),
        cnpj: novoGrupo.cnpj || undefined,
        gestor: novoGrupo.gestor || undefined,
        administrador: novoGrupo.administrador || undefined,
        tipo: novoGrupo.tipo || undefined,
      });
      setGruposFundo(prev => [...prev, criado]);
      setGrupoFundoSelecionado(criado);
      setMostrarNovoGrupo(false);
      setNovoGrupo(EMPTY_GRUPO);
    } catch {
      setErro('Erro ao criar grupo de fundo.');
    } finally {
      setSalvandoGrupo(false);
    }
  };

  const handleAtivarVincularExistente = () => {
    setVincularExistente(true);
    setAtivosMasterOpcoes([]);
    setAtivoExistenteSelecionado(null);
  };

  const handleAtivoMasterSearch = useCallback((_: React.SyntheticEvent, value: string) => {
    if (searchMasterTimerRef.current) clearTimeout(searchMasterTimerRef.current);
    if (value.length < 2) { setAtivosMasterOpcoes([]); setLoadingAtivosMaster(false); return; }
    setLoadingAtivosMaster(true);
    searchMasterTimerRef.current = setTimeout(async () => {
      try {
        setAtivosMasterOpcoes(await searchAtivosMaster(value));
      } catch {
        setErro('Erro ao buscar ativos.');
      } finally {
        setLoadingAtivosMaster(false);
      }
    }, 400);
  }, []);

  const saveCronogramas = async (ativoId: number) => {
    for (const id of deletedCronIds) {
      await deleteCronogramaAmortizacao(id);
    }
    for (const cron of cronogramas) {
      // Cronogramas existentes não modificados não geram PATCH
      if (cron.id && !cron._dirty) continue;

      const campos = {
        tipo:           cron.tipo as CronogramaTipo,
        descricao:      cron.descricao || null,
        periodo:        cron.periodo as CronogramaPeriodo,
        mes_ano_inicio: `${cron.mes_ano_inicio}-01`,
        mes_ano_fim:    cron.mes_ano_fim ? `${cron.mes_ano_fim}-01` : null,
        dia:            cron.dia ? parseInt(cron.dia, 10) : null,
      };
      if (cron.id) {
        await updateCronogramaAmortizacao(cron.id, campos);
      } else {
        await createCronogramaAmortizacao({ ativo_master: ativoId, ...campos });
      }
    }
  };

  const addCronograma = () => setCronogramas(prev => [...prev, EMPTY_CRON_ENTRY()]);

  const removeCronograma = (idx: number) => {
    const cron = cronogramas[idx];
    if (cron.id) setDeletedCronIds(prev => [...prev, cron.id!]);
    setCronogramas(prev => prev.filter((_, i) => i !== idx));
  };

  const updateCronograma = (idx: number, field: CronogramaEditableField, value: string) => {
    setCronogramas(prev => prev.map((c, i) => {
      if (i !== idx) return c;
      const updated = { ...c, [field]: value, _dirty: true };
      if (field === 'periodo' && value === 'NO_VENCIMENTO') updated.mes_ano_fim = '';
      return updated;
    }));
  };

  const handleSalvar = async () => {
    setSalvando(true);
    setErro(null);
    try {
      // Modo: vincular De-Para a ativo existente
      if (vincularExistente && ativoExistenteSelecionado && dadosCarteira) {
        const dp = await createDeParaAtivo({
          ativo_master: ativoExistenteSelecionado.id,
          administradora: dadosCarteira.administradora,
          codigo_bruto: dadosCarteira.codigo_bruto,
          nome_bruto: dadosCarteira.nome_bruto,
        });
        onSalvo({ ...ativoExistenteSelecionado, de_para: [...ativoExistenteSelecionado.de_para, dp] });
        onRequestClose();
        return;
      }

      if (!nomeCanonical.trim()) {
        setErro('Nome canônico é obrigatório.');
        setSalvando(false);
        return;
      }

      // Validação antecipada dos cronogramas
      if (condominio === 'FECHADO' && grupoFundoSelecionado) {
        for (let i = 0; i < cronogramas.length; i++) {
          const cron = cronogramas[i];
          const label = `#${i + 1}`;
          if (cron.tipo === 'PERSONALIZADO' && !cron.descricao.trim()) {
            setErro(`Informe a descrição do cronograma ${label} (tipo Personalizado).`);
            setSalvando(false);
            return;
          }
          if (!cron.periodo) {
            setErro(`Selecione o período do cronograma ${label}.`);
            setSalvando(false);
            return;
          }
          if (!cron.mes_ano_inicio) {
            setErro(`Informe o mês/ano de início do cronograma ${label}.`);
            setSalvando(false);
            return;
          }
          if (cron.periodo !== 'NO_VENCIMENTO' && !cron.mes_ano_fim) {
            setErro(`Informe o mês/ano de fim do cronograma ${label}.`);
            setSalvando(false);
            return;
          }
          if (cron.periodo !== 'NO_VENCIMENTO' && cron.mes_ano_fim && cron.mes_ano_fim < cron.mes_ano_inicio) {
            setErro(`A data de fim deve ser posterior à data de início no cronograma ${label}.`);
            setSalvando(false);
            return;
          }
        }
      }

      const payload = {
        nome_canonico: nomeCanonical.trim(),
        grupo_fundo: grupoFundoSelecionado?.id,
        classe_fidc: (grupoEhFidc && classeFidc) ? classeFidc : undefined,
        subclasse: subclasse || undefined,
        condominio: condominio || undefined,
        rent_tipo: rentTipo || null,
        rent_valor: rentPrecisaValor && rentValor ? parseFloat(rentValor) : null,
        rent_periodo: rentPeriodo,
        rent_indicador: rentPrecisaIndicador ? (rentIndicador?.id ?? null) : null,
        prazo_resgate: prazoResgate || null,
        observacoes: observacoes || null,
        cronograma_observacoes: cronogramaObservacoes || null,
      };

      let ativo: AtivoMaster;
      if (isEdicao && ativoMasterExistente) {
        ativo = await updateAtivoMaster(ativoMasterExistente.id, payload);
      } else {
        ativo = await createAtivoMaster(payload);
        if (dadosCarteira) {
          try {
            await createDeParaAtivo({
              ativo_master: ativo.id,
              administradora: dadosCarteira.administradora,
              codigo_bruto: dadosCarteira.codigo_bruto,
              nome_bruto: dadosCarteira.nome_bruto,
            });
          } catch {
            // silencia: De-Para pode já existir
          }
        }
      }

      if (condominio === 'FECHADO' && grupoFundoSelecionado) {
        await saveCronogramas(ativo.id);
      }

      onSalvo(ativo);
      onRequestClose();
    } catch (err) {
      setErro(getErrorMessage(err));
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Modal open={isOpen} onClose={onRequestClose}>
      <Box sx={modalStyle}>
        <Typography variant="h6" sx={{ fontFamily: 'Inter', fontWeight: 700, mb: 0.5 }}>
          {isEdicao ? 'Editar Ativo Master' : 'Cadastrar Ativo Master'}
        </Typography>

        {dadosCarteira && !isEdicao && (
          <>
            <Typography variant="body2" sx={{ color: '#8A98A8', mb: 1.5 }}>
              Vinculando: <strong>{dadosCarteira.nome_bruto}</strong>
              {' '}({dadosCarteira.administradora} / {dadosCarteira.codigo_bruto})
            </Typography>
            <Stack direction="row" spacing={1} mb={2}>
              <Button size="small"
                variant={!vincularExistente ? 'contained' : 'outlined'}
                onClick={() => { setVincularExistente(false); setAtivoExistenteSelecionado(null); }}
                sx={{ textTransform: 'none', fontSize: '12px' }}>
                Criar novo ativo
              </Button>
              <Button size="small"
                variant={vincularExistente ? 'contained' : 'outlined'}
                onClick={handleAtivarVincularExistente}
                sx={{ textTransform: 'none', fontSize: '12px' }}>
                Vincular a ativo já cadastrado
              </Button>
            </Stack>
          </>
        )}

        <Divider sx={{ mb: 3 }} />

        {/* Modo: vincular a ativo existente */}
        {vincularExistente && dadosCarteira && !isEdicao && (
          <Box>
            <Stack spacing={2}>
              <Autocomplete
                options={ativosMasterOpcoes}
                getOptionLabel={a => a.nome_canonico}
                value={ativoExistenteSelecionado}
                onChange={(_, v) => setAtivoExistenteSelecionado(v)}
                onInputChange={handleAtivoMasterSearch}
                loading={loadingAtivosMaster}
                filterOptions={x => x}
                noOptionsText="Digite 2+ caracteres para buscar"
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Buscar ativo já cadastrado *"
                    size="small"
                    placeholder="Digite o nome canônico..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingAtivosMaster ? <CircularProgress size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, a) => (
                  <li {...props} key={a.id}>
                    <Box>
                      <Typography variant="body2">{a.nome_canonico}</Typography>
                      {a.classe_fidc_display && (
                        <Typography variant="caption" sx={{ color: '#64748B' }}>
                          {a.classe_fidc_display}
                        </Typography>
                      )}
                    </Box>
                  </li>
                )}
                isOptionEqualToValue={(o, v) => o.id === v.id}
              />
              {ativoExistenteSelecionado && dadosCarteira && (
                <Alert severity="success" sx={{ fontSize: '13px' }}>
                  Será criado: <strong>{dadosCarteira.administradora} — {dadosCarteira.nome_bruto}</strong>
                  {' → '}<strong>{ativoExistenteSelecionado.nome_canonico}</strong>
                </Alert>
              )}
            </Stack>
          </Box>
        )}

        {!vincularExistente && loadingOpcoes && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!vincularExistente && !loadingOpcoes && (
          <Stack spacing={3}>
            {/* ── Grupo Fundo ── */}
            <Box>
              <Typography sx={sectionTitle}>Grupo / Fundo</Typography>
              <Stack spacing={2}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Autocomplete
                    options={gruposFundo}
                    getOptionLabel={g => `${g.nome}${g.tipo ? ` (${g.tipo})` : ''}`}
                    value={grupoFundoSelecionado}
                    onChange={(_, v) => { setGrupoFundoSelecionado(v); setClasseFidc(''); setSubclasse(''); }}
                    renderInput={params => <TextField {...params} label="Grupo de Fundo" size="small" />}
                    renderOption={(props, g) => (
                      <li {...props} key={g.id}>
                        <Box>
                          <Typography variant="body2">{g.nome}{g.tipo ? ` (${g.tipo})` : ''}</Typography>
                          {g.cnpj && (
                            <Typography variant="caption" sx={{ color: '#8A98A8', fontStyle: 'italic' }}>
                              {g.cnpj}
                            </Typography>
                          )}
                        </Box>
                      </li>
                    )}
                    sx={{ flex: 1 }}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{ whiteSpace: 'nowrap', height: 40 }}
                    onClick={() => setMostrarNovoGrupo(v => !v)}
                  >
                    {mostrarNovoGrupo ? 'Cancelar' : '+ Novo'}
                  </Button>
                </Stack>

                {grupoFundoSelecionado && (
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {grupoFundoSelecionado.tipo && (
                      <Chip label={grupoFundoSelecionado.tipo_display ?? grupoFundoSelecionado.tipo} size="small" />
                    )}
                    {grupoFundoSelecionado.gestor && (
                      <Chip label={`Gestor: ${grupoFundoSelecionado.gestor}`} size="small" variant="outlined" />
                    )}
                  </Stack>
                )}

                {/* Sub-formulário para criar novo GrupoFundo */}
                {mostrarNovoGrupo && (
                  <Box sx={{ p: 2, border: '1px solid #CFD8E5', borderRadius: 1, backgroundColor: '#F9FAFB' }}>
                    <Typography sx={{ ...sectionTitle, mt: 0 }}>Novo Grupo de Fundo</Typography>
                    <Stack spacing={1.5}>
                      <TextField
                        label="Nome *" size="small" fullWidth
                        value={novoGrupo.nome}
                        onChange={e => setNovoGrupo(p => ({ ...p, nome: e.target.value }))}
                      />
                      <Stack direction="row" spacing={1.5}>
                        <TextField
                          label="CNPJ" size="small" sx={{ flex: 1 }}
                          value={novoGrupo.cnpj}
                          onChange={e => setNovoGrupo(p => ({ ...p, cnpj: formatCNPJ(e.target.value) }))}
                          inputProps={{ maxLength: 18 }}
                          placeholder="00.000.000/0000-00"
                        />
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <InputLabel>Tipo</InputLabel>
                          <Select label="Tipo" value={novoGrupo.tipo}
                            onChange={e => setNovoGrupo(p => ({ ...p, tipo: e.target.value }))}>
                            <MenuItem value=""><em>—</em></MenuItem>
                            {TIPO_FUNDO_OPTIONS.map(o => (
                              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Stack>
                      <Stack direction="row" spacing={1.5}>
                        <TextField
                          label="Gestor" size="small" sx={{ flex: 1 }}
                          value={novoGrupo.gestor}
                          onChange={e => setNovoGrupo(p => ({ ...p, gestor: e.target.value }))}
                        />
                        <TextField
                          label="Administrador" size="small" sx={{ flex: 1 }}
                          value={novoGrupo.administrador}
                          onChange={e => setNovoGrupo(p => ({ ...p, administrador: e.target.value }))}
                        />
                      </Stack>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleCriarGrupo}
                        disabled={salvandoGrupo || !novoGrupo.nome.trim()}
                        sx={{ alignSelf: 'flex-end' }}
                      >
                        {salvandoGrupo ? <CircularProgress size={14} /> : 'Criar Grupo'}
                      </Button>
                    </Stack>
                  </Box>
                )}

                {/* Classe FIDC — só aparece quando tipo = FIDC */}
                {grupoEhFidc && (
                  <Stack direction="row" spacing={1.5}>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <InputLabel>Classe FIDC</InputLabel>
                      <Select
                        label="Classe FIDC"
                        value={classeFidc}
                        onChange={e => setClasseFidc(e.target.value as ClasseFIDC)}
                      >
                        <MenuItem value=""><em>—</em></MenuItem>
                        {CLASSE_FIDC_OPTIONS.map(o => (
                          <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      label="Subclasse / Série"
                      size="small"
                      sx={{ flex: 1 }}
                      placeholder="Ex: Série 1, Série 2"
                      value={subclasse}
                      onChange={e => setSubclasse(e.target.value)}
                    />
                  </Stack>
                )}


                {/* Condomínio — aparece quando há grupo de fundo selecionado */}
                {grupoFundoSelecionado && (
                  <FormControl size="small" sx={{ minWidth: 160, alignSelf: 'flex-start' }}>
                    <InputLabel>Condomínio</InputLabel>
                    <Select
                      label="Condomínio"
                      value={condominio}
                      onChange={e => setCondominio(e.target.value)}
                    >
                      <MenuItem value=""><em>— Não definido —</em></MenuItem>
                      {CONDOMINIO_OPTIONS.map(o => (
                        <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Stack>
            </Box>

            {/* ── Identificação ── */}
            <Box>
              <Typography sx={sectionTitle}>Identificação</Typography>
              <Stack spacing={2}>
                <TextField
                  label="Nome Canônico *"
                  size="small"
                  fullWidth
                  value={nomeCanonical}
                  onChange={e => setNomeCanonical(e.target.value)}
                  helperText={grupoFundoSelecionado && !isEdicao ? 'Preenchido automaticamente com o nome do grupo. Edite se necessário.' : undefined}
                />
              </Stack>
            </Box>

            {/* ── Rentabilidade Esperada ── */}
            <Box>
              <Typography sx={sectionTitle}>Rentabilidade Esperada</Typography>
              <Stack spacing={2}>
                <Stack direction="row" spacing={1.5} flexWrap="wrap">
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Tipo de Rentabilidade</InputLabel>
                    <Select
                      label="Tipo de Rentabilidade"
                      value={rentTipo}
                      onChange={e => { setRentTipo(e.target.value as RentTipo); setRentIndicador(null); }}
                    >
                      <MenuItem value=""><em>— Não definido —</em></MenuItem>
                      {RENT_TIPO_OPTIONS.map(o => (
                        <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {rentPrecisaValor && (
                    <>
                      <TextField
                        label={
                          rentTipo === 'FIXO' ? 'Taxa (%)' :
                          rentTipo === 'IND_PERCENT' ? 'Percentual (%)' :
                          'Spread (%)'
                        }
                        size="small"
                        type="number"
                        sx={{ width: 140 }}
                        value={rentValor}
                        onChange={e => setRentValor(e.target.value)}
                        inputProps={{ step: '0.01' }}
                      />
                      <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Período</InputLabel>
                        <Select
                          label="Período"
                          value={rentPeriodo}
                          onChange={e => setRentPeriodo(e.target.value as RentPeriodo)}
                        >
                          {RENT_PERIODO_OPTIONS.map(o => (
                            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </>
                  )}
                </Stack>

                {rentPrecisaIndicador && (
                  <Autocomplete
                    options={indicadores}
                    getOptionLabel={i => i.codigo}
                    value={rentIndicador}
                    onChange={(_, v) => setRentIndicador(v)}
                    renderInput={params => (
                      <TextField {...params} label="Indicador *" size="small" />
                    )}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                  />
                )}
              </Stack>
            </Box>

            {/* ── Cronograma de Amortização ── */}
            {condominio === 'FECHADO' && grupoFundoSelecionado && (
              <Box>
                <Typography sx={sectionTitle}>Cronograma de Amortização</Typography>
                {loadingCron ? (
                  <Box sx={{ display: 'flex', py: 2 }}><CircularProgress size={20} /></Box>
                ) : (
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                    <Stack spacing={1.5}>
                      <TextField
                        label="Descrição do cronograma (opcional)"
                        size="small"
                        fullWidth
                        multiline
                        minRows={2}
                        placeholder="Descreva as condições gerais do cronograma de amortização..."
                        value={cronogramaObservacoes}
                        onChange={e => setCronogramaObservacoes(e.target.value)}
                      />
                      {cronogramas.map((cron, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            p: 2,
                            border: '1px solid #90CAF9',
                            borderRadius: 1.5,
                            backgroundColor: '#F0F7FF',
                          }}
                        >
                          {/* Cabeçalho: tipo + botão remover */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                            <FormControl size="small" sx={{ width: 240 }}>
                              <InputLabel>Tipo *</InputLabel>
                              <Select
                                label="Tipo *"
                                value={cron.tipo}
                                onChange={e => updateCronograma(idx, 'tipo', e.target.value)}
                              >
                                {TIPO_OPTIONS.map(o => (
                                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <Box sx={{ flex: 1 }} />
                            <IconButton
                              size="small"
                              onClick={() => removeCronograma(idx)}
                              sx={{ color: '#E51739' }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Box>

                          {/* Descrição — só para PERSONALIZADO */}
                          {cron.tipo === 'PERSONALIZADO' && (
                            <TextField
                              label="Descrição *"
                              size="small"
                              fullWidth
                              placeholder="Ex: 1/6 Principal + Juros"
                              value={cron.descricao}
                              onChange={e => updateCronograma(idx, 'descricao', e.target.value)}
                              sx={{ mb: 1.5 }}
                            />
                          )}

                          {/* Frequência | Início | Fim | Dia — grid fixo, Início expande quando sem Fim */}
                          <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr 1fr 72px',
                            gap: 1.5,
                          }}>
                            <FormControl size="small" fullWidth>
                              <InputLabel>Frequência *</InputLabel>
                              <Select
                                label="Frequência *"
                                value={cron.periodo}
                                onChange={e => updateCronograma(idx, 'periodo', e.target.value)}
                              >
                                <MenuItem value=""><em>— Selecione —</em></MenuItem>
                                {PERIODO_OPTIONS.map(o => (
                                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <DatePicker
                              label="Início Pagamento *"
                              views={['year', 'month']}
                              format="MM/yyyy"
                              value={monthToDate(cron.mes_ano_inicio)}
                              onChange={d => updateCronograma(idx, 'mes_ano_inicio', dateToMonth(d))}
                              maxDate={cron.mes_ano_fim ? monthToDate(cron.mes_ano_fim) ?? undefined : undefined}
                              sx={{ width: '100%', gridColumn: cron.periodo === 'NO_VENCIMENTO' ? 'span 2' : 'span 1' }}
                              slotProps={{ textField: { size: 'small', fullWidth: true } }}
                            />
                            {cron.periodo !== 'NO_VENCIMENTO' && (
                              <DatePicker
                                label="Fim Pagamento *"
                                views={['year', 'month']}
                                format="MM/yyyy"
                                value={monthToDate(cron.mes_ano_fim)}
                                onChange={d => updateCronograma(idx, 'mes_ano_fim', dateToMonth(d))}
                                minDate={cron.mes_ano_inicio ? monthToDate(cron.mes_ano_inicio) ?? undefined : undefined}
                                sx={{ width: '100%' }}
                                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                              />
                            )}
                            <TextField
                              label="Dia"
                              type="number"
                              size="small"
                              fullWidth
                              value={cron.dia}
                              onChange={e => updateCronograma(idx, 'dia', e.target.value)}
                              inputProps={{ min: 1, max: 31 }}
                            />
                          </Box>
                        </Box>
                      ))}

                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={addCronograma}
                        sx={{ alignSelf: 'flex-start', textTransform: 'none', fontSize: '13px', borderColor: '#90CAF9', color: '#2C91AD' }}
                      >
                        Adicionar cronograma
                      </Button>
                    </Stack>
                  </LocalizationProvider>
                )}
              </Box>
            )}

            {/* ── Informações Adicionais ── */}
            <Box>
              <Typography sx={sectionTitle}>Informações Adicionais</Typography>
              <Stack spacing={2}>
                <TextField
                  label="Prazo de Resgate"
                  size="small"
                  fullWidth
                  placeholder="Ex: D+30, D+90, Fechado"
                  value={prazoResgate}
                  onChange={e => setPrazoResgate(e.target.value)}
                />
                <TextField
                  label="Observações"
                  size="small"
                  fullWidth
                  multiline
                  rows={2}
                  value={observacoes}
                  onChange={e => setObservacoes(e.target.value)}
                />
              </Stack>
            </Box>
          </Stack>
        )}

        {erro && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {erro}
          </Alert>
        )}

        <Divider sx={{ mt: 3, mb: 2 }} />

        <Stack direction="row" spacing={1.5} justifyContent="flex-end">
          <Button variant="outlined" onClick={onRequestClose} disabled={salvando}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSalvar}
            disabled={
              salvando ||
              (vincularExistente ? (loadingAtivosMaster || !ativoExistenteSelecionado) : (loadingOpcoes || !nomeCanonical.trim()))
            }
          >
            {salvando ? <CircularProgress size={18} /> : vincularExistente ? 'Vincular' : isEdicao ? 'Salvar Alterações' : 'Cadastrar'}
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
};

export default CadastrarAtivoMasterModal;
